import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Image, Pressable, View, Platform, InteractionManager, Keyboard } from 'react-native';
import { useRouter } from 'expo-router';
import { format } from 'date-fns';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { OnboardingProgress } from '@/components/onboarding/OnboardingProgress';
import { getOnboardingStep, getTotalOnboardingSteps } from '@/constants/onboarding';
import { ONBOARDING_FEATURED_TEMPLATES } from '@/constants/onboardingFeaturedTemplates';
import { LoadingState } from '@/components/ui/LoadingState';
import { useOnboardingStepAnalytics } from '@/hooks/useOnboardingStepAnalytics';
import {
  normalizeTemplateIdParam,
  normalizeTemplateTopicParam,
} from '@/navigation/templateDeepLink';
import {
  consumePendingNavigationIntent,
  getPendingNavigationIntent,
} from '@/navigation/pendingNavigationIntent';
import { markOnboardingComplete } from '@/services/onboardingCompletion';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import {
  loadOnboardingFeaturedTemplates,
  resolveOnboardingTemplateId,
} from '@/services/loadOnboardingFeaturedTemplates';
import { templateHasTopicTag } from '@/services/onboardingFeaturedTemplateMatching';
import { inferDefaultTemplateStartDate } from '@/services/templateDefaultStartDate';
import { createTripFromTemplate } from '@/services/tripTemplates';
import { useTripSelectionStore } from '@/state/tripSelectionStore';
import type { TripTemplate } from '@/types/tripTemplates';

function getPendingTemplateDeeplink(): { templateId?: string; topicTag?: string } {
  const intent = getPendingNavigationIntent();
  if (intent?.kind !== 'route' || intent.pathname !== '/(tabs)') {
    return {};
  }
  return {
    templateId: normalizeTemplateIdParam(intent.params?.templateId as string | string[] | undefined),
    topicTag: normalizeTemplateTopicParam(intent.params?.templateTopic as string | string[] | undefined),
  };
}

function featuredRefForTemplate(template: TripTemplate) {
  return (
    ONBOARDING_FEATURED_TEMPLATES.find((entry) => entry.id === template.id) ??
    ONBOARDING_FEATURED_TEMPLATES.find((entry) => templateHasTopicTag(template, entry.topicTag))
  );
}

function topicTagForTemplate(template: TripTemplate): string | undefined {
  return featuredRefForTemplate(template)?.topicTag;
}

export default function FirstTripOnboardingScreen() {
  const router = useRouter();
  const theme = useTheme();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const isNewUser = useUserStore((state) => state.isNewUser);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const authReady = useUserStore((state) => state.authReady);
  const { logCompleted, logSkipped } = useOnboardingStepAnalytics('first-trip');

  const pendingDeeplink = useMemo(() => getPendingTemplateDeeplink(), []);
  const [templates, setTemplates] = useState<TripTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(pendingDeeplink.templateId ?? null);
  const [selectedTopicTag, setSelectedTopicTag] = useState<string | undefined>(pendingDeeplink.topicTag);
  const [creating, setCreating] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const loadTemplates = useCallback(async () => {
    if (!authReady || !firebaseUser?.uid) {
      return;
    }

    setLoadingTemplates(true);
    setError(null);

    try {
      const { templates: loaded, firestoreBackedCount } = await loadOnboardingFeaturedTemplates({
        pendingTemplateId: pendingDeeplink.templateId,
        pendingTopicTag: pendingDeeplink.topicTag,
      });

      setTemplates(loaded);

      if (pendingDeeplink.templateId) {
        const match = loaded.find((template) => template.id === pendingDeeplink.templateId);
        if (match) {
          setSelectedTemplateId(match.id);
          setSelectedTopicTag(pendingDeeplink.topicTag ?? topicTagForTemplate(match));
        }
      } else if (loaded.length > 0) {
        setSelectedTemplateId((current) => current ?? loaded[0].id);
        setSelectedTopicTag((current) => current ?? topicTagForTemplate(loaded[0]));
      }

      if (loaded.length === 0) {
        setError('Could not load trip templates. Check your connection and tap Retry.');
      } else if (firestoreBackedCount === 0) {
        setError(
          'Preview itineraries loaded. Connect to finish setup — trip creation needs live templates from the server.',
        );
      }
    } catch (loadError) {
      console.error('[FirstTripOnboarding] Failed to load templates', loadError);
      setError('Could not load trip templates. Check your connection and tap Retry.');
    } finally {
      setLoadingTemplates(false);
    }
  }, [
    authReady,
    firebaseUser?.uid,
    pendingDeeplink.templateId,
    pendingDeeplink.topicTag,
  ]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates, reloadToken]);

  useEffect(() => {
    if (authReady && !isNewUser && hasCompletedOnboarding) {
      router.replace('/(tabs)');
    }
  }, [authReady, hasCompletedOnboarding, isNewUser, router]);

  if (!authReady || !firebaseUser?.uid) {
    return <LoadingState variant="fullscreen" message="Loading templates…" />;
  }

  if (authReady && !isNewUser && hasCompletedOnboarding) {
    return <LoadingState variant="fullscreen" message="Opening Tripvio…" />;
  }

  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId) ?? null;
  const startDate = selectedTemplate
    ? inferDefaultTemplateStartDate(selectedTemplate, selectedTopicTag)
    : null;

  const navigateAfterSettledUi = async (target: string) => {
    Keyboard.dismiss();
    await new Promise<void>((resolve) => {
      InteractionManager.runAfterInteractions(() => resolve());
    });
    router.replace(target as never);
  };

  const handleSelectTemplate = (template: TripTemplate) => {
    setSelectedTemplateId(template.id);
    setSelectedTopicTag(topicTagForTemplate(template) ?? pendingDeeplink.topicTag);
    setError(null);
  };

  const handleCreateTrip = async () => {
    if (!firebaseUser?.uid || !selectedTemplate || !startDate || creating || finishing) {
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const featuredRef = featuredRefForTemplate(selectedTemplate);
      if (!featuredRef) {
        setError('Select one of the featured itineraries to continue.');
        return;
      }

      const templateId = await resolveOnboardingTemplateId(featuredRef);
      const tripId = await createTripFromTemplate(
        firebaseUser.uid,
        templateId,
        {
          startDate,
          name: selectedTemplate.title,
        },
        {
          source: pendingDeeplink.templateId ? 'deeplink' : 'browser',
          topicTag: selectedTopicTag ?? featuredRef.topicTag,
        },
      );

      consumePendingNavigationIntent();
      await markOnboardingComplete(firebaseUser.uid);
      logCompleted();
      useTripSelectionStore.getState().rememberTripId(tripId);

      void logAnalyticsEvent('onboarding_first_trip_created', {
        templateId: selectedTemplate.id,
        tripId,
        source: pendingDeeplink.templateId ? 'deeplink_picker' : 'picker',
        topicTag: selectedTopicTag,
      });

      router.replace({
        pathname: '/(tabs)/plan',
        params: { tripId },
      } as never);
    } catch (createError) {
      console.error('[FirstTripOnboarding] Failed to create trip', createError);
      setError(
        createError instanceof Error
          ? createError.message
          : 'Could not create your trip. Try again or skip for now.',
      );
    } finally {
      setCreating(false);
    }
  };

  const handleSkip = async () => {
    if (finishing || creating) {
      return;
    }
    setFinishing(true);
    logSkipped('user_skip');
    await markOnboardingComplete(firebaseUser?.uid);
    await navigateAfterSettledUi('/(tabs)');
  };

  const handleImportEmail = async () => {
    if (finishing || creating) {
      return;
    }
    await navigateAfterSettledUi('/(onboarding)/email-sync');
  };

  const currentStep = getOnboardingStep('first-trip') ?? 4;
  const totalSteps = getTotalOnboardingSteps();
  const templateEyebrow = (templateId: string) =>
    ONBOARDING_FEATURED_TEMPLATES.find((entry) => entry.id === templateId)?.eyebrow;

  return (
    <Screen
      title="Start from a proven itinerary"
      subtitle="Pick a curated template to create your first trip. You can customize everything after."
      showBackButton
      onBack={() => router.replace('/(onboarding)/travel-type')}
      scrollable
    >
      <OnboardingProgress currentStep={currentStep} totalSteps={totalSteps} />

      {loadingTemplates ? (
        <View style={{ paddingVertical: theme.spacing(4), alignItems: 'center' }}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      ) : templates.length > 0 ? (
        <View style={{ gap: theme.spacing(1.5) }}>
          {templates.map((template) => {
            const isSelected = template.id === selectedTemplateId;
            return (
              <Pressable
                key={template.id}
                accessibilityRole={Platform.OS === 'android' ? 'button' : 'radio'}
                accessibilityState={{ selected: isSelected }}
                onPress={() => handleSelectTemplate(template)}
                style={({ pressed }) => ({
                  borderRadius: theme.radii.lg,
                  borderWidth: 1,
                  borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                  backgroundColor: isSelected ? theme.colors.surface : theme.colors.background,
                  overflow: 'hidden',
                  opacity: pressed ? 0.92 : 1,
                })}
              >
                {template.coverImage ? (
                  <Image
                    source={{ uri: template.coverImage }}
                    style={{ width: '100%', height: 128 }}
                    resizeMode="cover"
                  />
                ) : null}
                <View style={{ padding: theme.spacing(1.75), gap: theme.spacing(0.5) }}>
                  {templateEyebrow(template.id) ? (
                    <Caption color={theme.colors.primary}>{templateEyebrow(template.id)}</Caption>
                  ) : null}
                  <ThemedText variant="title">{template.title}</ThemedText>
                  <Caption color={theme.colors.textSecondary}>
                    {template.destination} · {template.duration} days
                  </Caption>
                  {template.shortDescription ? (
                    <Body color={theme.colors.textSecondary}>{template.shortDescription}</Body>
                  ) : null}
                </View>
              </Pressable>
            );
          })}
        </View>
      ) : null}

      {startDate ? (
        <Caption color={theme.colors.textSecondary}>
          Suggested start: {format(startDate, 'EEE, MMM d, yyyy')}
        </Caption>
      ) : null}

      {error ? <Body color={theme.colors.error}>{error}</Body> : null}

      {!loadingTemplates && templates.length === 0 ? (
        <Button
          label="Retry"
          variant="secondary"
          onPress={() => setReloadToken((value) => value + 1)}
          disabled={creating || finishing}
        />
      ) : null}

      <Button
        label={creating ? 'Creating your trip…' : 'Create my trip'}
        onPress={handleCreateTrip}
        disabled={!selectedTemplate || loadingTemplates || creating || finishing}
      />
      <Button
        label="Import bookings from email"
        variant="secondary"
        onPress={handleImportEmail}
        disabled={creating || finishing}
      />
      <Button
        label="Skip for now"
        variant="ghost"
        onPress={handleSkip}
        disabled={creating || finishing}
      />
    </Screen>
  );
}
